// No action in the TPRM console is silent (release 3.1, R3-OI-107, the TPRM
// half of what FOCTTA Core's console got in Consent PR #162).
//
// # WHAT WAS WRONG
//
// Most screens here said nothing when a change worked (the row just changed,
// or did not) and showed a failure as a paragraph of the server's words with
// no code and nothing support could look up. A person who pressed Save could
// not tell whether anything had happened, and a person reporting a failure had
// nothing to quote.
//
// # WHAT THIS IS
//
// The console-wide safety net. It does not replace a screen's own words; it
// makes sure there always are some, and that every failure carries a stable
// code and a reference:
//
//  1. Every change the console sends (any request that is not a read, and any
//     download a person asked for) is watched from lib/api.ts request().
//  2. It WORKED and the screen raised no toast of its own: a short
//     confirmation, named where this file knows the act ("Vendor approved"),
//     otherwise "Done". Several changes settling together make one toast.
//  3. It FAILED and the screen showed nothing: one toast with a plain heading,
//     the server's words, the error code and the reference, and a Copy button.
//  4. It FAILED and the screen showed it inline (ErrorBox, or anything that
//     calls markErrorShown): no toast; the inline message carries the code
//     and the reference itself.
//  5. It FAILED and the screen raised its own toast: that toast gains the code
//     and the reference under its words; the net adds none of its own.
//
// "Its own toast" is measured, not guessed: the net waits SETTLE_MS after the
// request settles (a screen's catch and its setState have run by then) and
// looks at what was actually raised. A screen toast that arrives even later
// replaces the net's rather than joining it, so one outcome is one toast.
//
// Reads (GET) are not actions: a screen that fails to load shows its own error
// box, which carries the code and the reference too.
//
// The reference is the request id the API puts on every answer (X-Request-ID,
// and requestId in every error body since release 3.1). The same value is on
// the server's log line for that request, so support finds what happened from
// it alone. "No reference" means the request never reached the server.

import type { ApiError } from './api'

export const SETTLE_MS = 350
const REPLACE_WINDOW_MS = 4000
const SUCCESS_TOAST_MS = 4200
const ERROR_TOAST_MS = 12000

// ------------------------------------------------------------------ toasts

export type ToastKind = 'ok' | 'warn'

export type ToastItem = {
  id: number
  kind: ToastKind
  // A short heading, for the net's own error toasts.
  title?: string
  text: string
  code?: string
  reference?: string
  // Raised by this module rather than by a screen.
  fallback: boolean
  at: number
}

type Listener = (items: ToastItem[]) => void

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<Listener>()

function now(): number {
  return Date.now()
}

function emit(): void {
  for (const l of listeners) l(items)
}

export function subscribeToasts(l: Listener): () => void {
  listeners.add(l)
  l(items)
  return () => {
    listeners.delete(l)
  }
}

export function currentToasts(): ToastItem[] {
  return items
}

export function dismissToast(id: number): void {
  const before = items.length
  items = items.filter((t) => t.id !== id)
  if (items.length !== before) emit()
}

function add(t: Omit<ToastItem, 'id' | 'at'>, ms: number): ToastItem {
  const item: ToastItem = { ...t, id: nextId++, at: now() }
  items = [...items, item]
  emit()
  globalThis.setTimeout(() => dismissToast(item.id), ms)
  return item
}

// ------------------------------------------------------ what an error says

// The code and reference of any failure. A failure that is not the API's
// happened in the browser, before anything was sent.
export function errorInfoOf(err: unknown): { code: string; reference: string } {
  const e = err as Partial<ApiError> | null
  if (e && typeof e === 'object' && typeof e.code === 'string' && typeof e.status === 'number') {
    return { code: e.code || 'HTTP_' + e.status, reference: e.requestId || '' }
  }
  return { code: 'CLIENT_ERROR', reference: '' }
}

// A heading for a failure, by what kind of failure it is.
export function errorTitleOf(err: unknown): string {
  const e = err as Partial<ApiError> | null
  if (!e || typeof e !== 'object' || typeof e.status !== 'number') return 'That did not work'
  const s = e.status
  if (s === 0) return 'No answer from the server'
  if (s === 400 || s === 422) return 'Some details need fixing'
  if (s === 401) return 'Your session has ended'
  if (s === 403) return 'Not allowed'
  if (s === 404) return 'Not found'
  if (s === 408 || s === 504) return 'The server took too long'
  if (s === 409) return 'Nothing was changed'
  if (s === 410) return 'No longer available'
  if (s === 413) return 'Too large'
  if (s === 429) return 'Too many attempts'
  if (s === 501) return 'Not offered on this installation'
  if (s >= 500) return 'Something went wrong on the server'
  return 'That did not work'
}

export function errorTextOf(err: unknown): string {
  const e = err as Partial<ApiError> | null
  if (e && typeof e === 'object' && typeof e.message === 'string' && e.message) {
    if (typeof e.status === 'number' && e.status >= 500 && !/reference/i.test(e.message)) {
      return e.message + ' Try again; if it keeps happening, give support the reference below.'
    }
    return e.message
  }
  if (err instanceof Error && err.message) return err.message
  return 'Something went wrong in the browser, before anything was sent to the server.'
}

// The line under a failure's words: what support needs.
export function referenceLine(code: string, reference: string): string {
  return 'Error code: ' + code + '   Reference: ' + (reference || 'No reference')
}

// ------------------------------------------------ failures shown in place

const shown = new WeakSet<object>()

// A screen that shows a failure inline says so, and the net stays quiet about
// it. ErrorBox calls this for every failure it shows.
export function markErrorShown(err: unknown): void {
  if (err && typeof err === 'object') shown.add(err)
}

export function isErrorShown(err: unknown): boolean {
  return !!err && typeof err === 'object' && shown.has(err)
}

// ---------------------------------------------------- watching the actions

export type Action = {
  id: number
  method: string
  path: string
  startedAt: number
}

// byNet: the net's own toast is what said it, so a screen's toast arriving
// later replaces that one.
type Failure = { action: Action; error: ApiError; at: number; toastId?: number; claimed: boolean; byNet?: boolean }

let nextAction = 1
const recentFailures: Failure[] = []
let pendingSuccesses: Action[] = []
let successTimer: ReturnType<typeof setTimeout> | null = null

function prune(): void {
  const cutoff = now() - 30000
  while (recentFailures.length > 0 && (recentFailures[0]?.at ?? 0) < cutoff) recentFailures.shift()
}

// Whether a screen raised a toast of its own since an action began.
function screenToastSince(t: number): boolean {
  return items.some((i) => !i.fallback && i.at >= t)
}

export function beginAction(method: string, path: string): Action {
  return { id: nextAction++, method: method.toUpperCase(), path, startedAt: now() }
}

export function actionSucceeded(a: Action): void {
  if (isQuietOnSuccess(a.method, a.path)) return
  pendingSuccesses.push(a)
  if (successTimer !== null) return
  successTimer = globalThis.setTimeout(flushSuccesses, SETTLE_MS)
}

function flushSuccesses(): void {
  successTimer = null
  const batch = pendingSuccesses
  pendingSuccesses = []
  const unsaid = batch.filter((a) => !screenToastSince(a.startedAt))
  if (unsaid.length === 0) return
  const last = unsaid[unsaid.length - 1] as Action
  const text = unsaid.length === 1 ? successMessage(last.method, last.path) : unsaid.length + ' changes saved.'
  add({ kind: 'ok', text, fallback: true }, SUCCESS_TOAST_MS)
}

// A dialog was closed while a save sent from it was still out (the frame lets
// Close and Escape through, kit/Dialog.tsx). The request goes on. If it fails,
// the dialog that would have shown the failure is gone, so the toast that
// tells it also says how to get back.
let left: { name: string | null; at: number } | null = null

export function closedMidSave(name: string | null): void {
  left = { name, at: now() }
}

export function wayBack(name: string | null): string {
  return (name ? '"' + name + '"' : 'The dialog') + ' was closed before this answer came back. Open it again to try once more.'
}

// Whether a change was already out when a dialog was closed on it.
function leftBehind(a: Action): boolean {
  return left !== null && a.startedAt <= left.at && now() - left.at < 120000
}

export function actionFailed(a: Action | null, error: ApiError): void {
  prune()
  const f: Failure = { action: a ?? beginAction('GET', ''), error, at: now(), claimed: false }
  recentFailures.push(f)
  globalThis.setTimeout(() => {
    if (f.claimed || isErrorShown(error)) return
    // The screen raised its own toast for this failure: interceptScreenToast
    // gave it the code and reference and claimed the failure.
    const t = add(
      {
        kind: 'warn',
        title: errorTitleOf(error),
        text: errorTextOf(error) + (a && left && leftBehind(a) ? ' ' + wayBack(left.name) : ''),
        ...errorInfoOf(error),
        fallback: true,
      },
      ERROR_TOAST_MS,
    )
    f.toastId = t.id
    f.claimed = true
    f.byNet = true
  }, SETTLE_MS)
}

// A toast a screen raises. A warning is matched to the failure it is about (its
// words, or else the most recent failure nobody has said anything about) and
// carries that failure's code and reference. If the net already toasted that
// failure, the screen's toast replaces the net's.
export function pushScreenToast(text: string, kind: ToastKind = 'ok'): void {
  prune()
  if (kind !== 'warn') {
    add({ kind, text, fallback: false }, SUCCESS_TOAST_MS)
    return
  }
  const f = failureFor(text)
  if (f) {
    const replacing = f.toastId !== undefined && now() - f.at < REPLACE_WINDOW_MS ? f.toastId : undefined
    if (replacing !== undefined && f.byNet) dismissToast(replacing)
    f.claimed = true
    f.byNet = false
    const t = add({ kind, text, ...errorInfoOf(f.error), fallback: false }, ERROR_TOAST_MS)
    f.toastId = t.id
    return
  }
  // The same words already on screen: one failure, one message.
  if (items.some((i) => i.kind === 'warn' && i.text === text && now() - i.at < REPLACE_WINDOW_MS)) return
  add({ kind, text, fallback: false }, ERROR_TOAST_MS)
}

function failureFor(text: string): Failure | undefined {
  for (let i = recentFailures.length - 1; i >= 0; i--) {
    const f = recentFailures[i] as Failure
    const m = f.error.message
    if (m && (text.includes(m) || (text.length > 12 && m.includes(text)))) return f
  }
  // No words match: the failure that settled last and nobody has spoken for,
  // if it settled a moment ago.
  for (let i = recentFailures.length - 1; i >= 0; i--) {
    const f = recentFailures[i] as Failure
    if ((!f.claimed || f.byNet) && now() - f.at < REPLACE_WINDOW_MS) return f
  }
  return undefined
}

// ------------------------------------------------ which changes say what

// Changes whose result the screen shows where the person is looking (a test
// result, a preview, a validation, a notice marked read), or reads that go by
// POST. Their success is quiet; their failure is not.
const QUIET_ON_SUCCESS: RegExp[] = [
  /^POST \/v1\/vendors\/validate$/,
  // The tier Add a vendor shows beside the form as the person answers: a read
  // by POST, asked on every pause, never a change (release 3.3, M6).
  /^POST \/v1\/vendors\/tier-preview$/,
  /^POST \/v1\/intent\/compile$/,
  /^POST \/v1\/risk-model-versions\/[^/]+\/(validate|explain)$/,
  /^POST \/v1\/settings\/identity\/persona-mappings\/preview$/,
  /^POST \/v1\/notices\/[^/]+\/read$/,
  /^POST \/v1\/exit-dossiers\/verify$/,
  /^POST \/v1\/connectors\/[^/]+\/(test|send-test|send-test-message)$/,
  /^POST \/v1\/risk-(registers|sinks)\/[^/]+\/test$/,
  /^POST \/v1\/vendor-registry\/subscribers\/[^/]+\/test$/,
  /^POST \/v1\/settings\/(platform-link|relay)\/test$/,
  /^POST \/v1\/auth\//,
  // The import wizard's reads by POST: what the file holds, the values to
  // match, the counts a matching gives and which vendor each document belongs
  // to. Each answer is the next thing on the screen; nothing is added yet.
  /^POST \/v1\/imports\/analyse$/,
  /^POST \/v1\/imports\/[^/]+\/(values|mapping|documents\/match)$/,
  // The bulk send's proposal: a read by POST, shown as the next step.
  /^POST \/v1\/baselines\/propose$/,
  // The import's draft is written as the person works (every Next, and a
  // second after a change): the step's footer says "Draft saved" quietly, so a
  // toast for each would be noise.
  /^PUT \/v1\/imports\/[^/]+\/draft$/,
  /^GET /,
]

export function isQuietOnSuccess(method: string, path: string): boolean {
  const key = method.toUpperCase() + ' ' + path.split('?')[0]
  return QUIET_ON_SUCCESS.some((r) => r.test(key))
}

// The words for a change that worked, by what it was. Checked in order; the
// first match names it. Anything not named here is "Done."
const NAMED: [RegExp, string][] = [
  [/^POST \/v1\/vendors$/, 'Vendor added to the register as a draft.'],
  [/^PUT \/v1\/vendors\/[^/]+$/, 'Vendor saved.'],
  [/^POST \/v1\/vendors\/[^/]+\/submit$/, 'Vendor sent for approval.'],
  [/^POST \/v1\/vendors\/[^/]+\/return$/, 'Vendor returned for more work.'],
  [/^POST \/v1\/vendors\/[^/]+\/suspend$/, 'Vendor suspended.'],
  [/^POST \/v1\/vendors\/[^/]+\/approve$/, 'Vendor approved into the register.'],
  [/^POST \/v1\/vendors\/[^/]+\/reject$/, 'Vendor rejected.'],
  [/^POST \/v1\/vendors\/[^/]+\/approve-request$/, 'Request approved. Due diligence may begin.'],
  [/^POST \/v1\/vendors\/[^/]+\/reject-request$/, 'Request rejected.'],
  [/^PUT \/v1\/settings\/vendor-requests$/, 'Setting saved.'],
  [/^PUT \/v1\/baseline-rules$/, 'Baseline rules saved.'],
  [/^POST \/v1\/vendor-groups$/, 'Group added.'],
  [/^PUT \/v1\/vendor-groups\/[^/]+$/, 'Group saved.'],
  [/^DELETE \/v1\/vendor-groups\/[^/]+$/, 'Group retired.'],
  [/^POST \/v1\/vendor-groups\/[^/]+\/members$/, 'Vendors added to the group.'],
  [/^DELETE \/v1\/vendor-groups\/[^/]+\/members\/[^/]+$/, 'Vendor taken out of the group.'],
  [/^POST \/v1\/campaign-batches$/, 'Bulk send created. Nothing is sent until a second person approves it.'],
  [/^POST \/v1\/campaign-batches\/[^/]+\/approve$/, 'Bulk send approved.'],
  [/^POST \/v1\/vendors\/[^/]+\/resume$/, 'Vendor resumed.'],
  [/^POST \/v1\/vendors\/[^/]+\/offboard$/, 'Offboarding started.'],
  [/^POST \/v1\/vendors\/[^/]+\/offboarded$/, 'Offboarding confirmed complete.'],
  [/^POST \/v1\/vendors\/[^/]+\/approvals$/, 'Your signature is recorded.'],
  [/^POST \/v1\/vendors\/[^/]+\/contacts$/, 'Contact added.'],
  [/^PUT \/v1\/contacts\/[^/]+$/, 'Contact saved.'],
  [/^POST \/v1\/contacts\/[^/]+\/deactivate$/, 'Contact switched off.'],
  [/^POST \/v1\/contacts\/[^/]+\/erase$/, "Contact's personal data erased."],
  [/^POST \/v1\/vendors\/[^/]+\/custom-fields\/erase$/, 'Personal data in the custom fields erased.'],
  [/^PUT \/v1\/vendors\/[^/]+\/custom-fields$/, 'Custom fields saved.'],
  [/^PUT \/v1\/vendors\/[^/]+\/data-processing$/, 'Data processing saved.'],
  [/^POST \/v1\/vendors\/[^/]+\/subprocessors$/, 'Subprocessor added.'],
  [/^PUT \/v1\/vendors\/[^/]+\/subprocessors\/[^/]+$/, 'Subprocessor saved.'],
  [/^POST \/v1\/vendors\/[^/]+\/subprocessors\/[^/]+\/deactivate$/, 'Subprocessor switched off.'],
  [/^POST \/v1\/vendors\/[^/]+\/classify$/, 'Tier worked out again.'],
  [/^POST \/v1\/vendors\/[^/]+\/tier-override$/, 'Tier change requested; a second person approves it.'],
  [/^POST \/v1\/vendors\/[^/]+\/tier-override\/[^/]+\/approve$/, 'Tier change approved.'],
  [/^POST \/v1\/vendors\/[^/]+\/tier-override\/[^/]+\/reject$/, 'Tier change rejected.'],
  [/^POST \/v1\/vendors\/[^/]+\/tier-override\/[^/]+\/withdraw$/, 'Tier change withdrawn.'],
  [/^POST \/v1\/vendors\/[^/]+\/documents$/, 'Document filed.'],
  [/^POST \/v1\/documents\/[^/]+\/extract$/, 'Document read; check what it proposes.'],
  [/^POST \/v1\/documents\/[^/]+\/proposals\/confirm$/, 'Answers confirmed.'],
  [/^POST \/v1\/documents\/[^/]+\/accept$/, 'Document accepted.'],
  [/^POST \/v1\/documents\/[^/]+\/decline$/, 'Document declined.'],
  [/^POST \/v1\/vendors\/[^/]+\/findings$/, 'Finding raised.'],
  [/^PATCH \/v1\/findings\/[^/]+$/, 'Finding saved.'],
  [/^POST \/v1\/findings\/[^/]+\/resolve$/, 'Finding resolved.'],
  [/^POST \/v1\/vendors\/[^/]+\/incidents$/, 'Incident recorded.'],
  [/^PATCH \/v1\/incidents\/[^/]+$/, 'Incident saved.'],
  [/^POST \/v1\/vendors\/[^/]+\/invite$/, 'Questionnaire sent.'],
  [/^POST \/v1\/vendors\/[^/]+\/reassess$/, 'Reassessment sent.'],
  [/^POST \/v1\/assessments\/[^/]+\/resend-invitation$/, 'Invitation sent again.'],
  [/^POST \/v1\/assessments\/[^/]+\/score$/, 'Assessment scored.'],
  [/^POST \/v1\/assessments\/[^/]+\/clarifications$/, 'Clarification asked.'],
  [/^POST \/v1\/clarifications\/[^/]+\/(accept|reject|withdraw)$/, 'Clarification recorded.'],
  [/^POST \/v1\/campaigns$/, 'Campaign drafted. Nothing is sent until it is approved.'],
  [/^POST \/v1\/campaigns\/[^/]+\/approve$/, 'Campaign approved and issued.'],
  [/^POST \/v1\/campaigns\/[^/]+\/cancel$/, 'Campaign cancelled.'],
  [/^POST \/v1\/exceptions$/, 'Exception raised.'],
  [/^POST \/v1\/exceptions\/[^/]+\/(approve|decide)$/, 'Your decision is recorded.'],
  [/^POST \/v1\/exceptions\/[^/]+\/(reject|remediate|review|route|clarification|close)$/, 'Exception updated.'],
  [/^POST \/v1\/questionnaires$/, 'Questionnaire drafted.'],
  [/^POST \/v1\/questionnaires\/import$/, 'Questionnaire imported as a draft.'],
  [/^POST \/v1\/questionnaires\/[^/]+\/versions$/, 'New draft version started.'],
  [/^POST \/v1\/questionnaires\/[^/]+\/(archive|restore)$/, 'Questionnaire updated.'],
  [/^PUT \/v1\/questionnaire-versions\/[^/]+$/, 'Draft saved.'],
  [/^POST \/v1\/questionnaire-versions\/[^/]+\/publish$/, 'Questionnaire version published.'],
  [/^POST \/v1\/risk-models$/, 'Risk model drafted.'],
  [/^POST \/v1\/risk-models\/[^/]+\/versions$/, 'New draft version started.'],
  [/^PUT \/v1\/risk-model-versions\/[^/]+$/, 'Draft saved.'],
  [/^POST \/v1\/risk-model-versions\/[^/]+\/promote$/, 'Risk model version put into service.'],
  [/^POST \/v1\/scores\/[^/]+\/override$/, 'Band override recorded.'],
  [/^POST \/v1\/imports$/, 'File read. Check the rows before committing them.'],
  [/^POST \/v1\/imports\/[^/]+\/commit$/, 'Vendors imported to the register.'],
  [/^POST \/v1\/imports\/[^/]+\/save-mapping$/, 'Mapping saved for the next file with these columns.'],
  [/^PATCH \/v1\/imports\/[^/]+\/rows\/[^/]+$/, 'Saved. Nothing is added until you import the vendors.'],
  [/^POST \/v1\/imports\/[^/]+\/fix-all$/, 'Fixed in every row with the same value.'],
  [/^POST \/v1\/imports\/[^/]+\/documents$/, 'Documents attached to their vendors.'],
  [/^POST \/v1\/imports\/[^/]+\/undo$/, 'Import undone. The details are kept and can be restored.'],
  [/^POST \/v1\/imports\/[^/]+\/restore$/, 'Import restored to the register.'],
  [/^POST \/v1\/imports\/[^/]+\/promote-columns$/, 'Columns promoted to custom fields.'],
  [/^(POST|PUT) \/v1\/custom-fields(\/[^/]+)?$/, 'Custom field saved.'],
  [/^(POST|PUT) \/v1\/approval-matrix-versions(\/[^/]+)?$/, 'Draft saved. A second person puts it into force.'],
  [/^POST \/v1\/approval-matrix-versions\/[^/]+\/promote$/, 'Approval matrix put into force.'],
  [/^(POST|PUT) \/v1\/onboarding-profile-versions(\/[^/]+)?$/, 'Draft saved. A second person puts it into force.'],
  [/^POST \/v1\/onboarding-profile-versions\/[^/]+\/promote$/, 'Onboarding steps put into force.'],
  [/^(POST|PUT) \/v1\/exception-authority-versions(\/[^/]+)?$/, 'Draft saved. A second person puts it into force.'],
  [/^POST \/v1\/exception-authority-versions\/[^/]+\/promote$/, 'Who can accept risks: new version put into force.'],
  [/^PUT \/v1\/settings\/due-diligence-parameters$/, 'Exception parameters saved.'],
  [/^PUT \/v1\/risk-matrix$/, 'Risk matrix saved.'],
  [/^POST \/v1\/risk-matrix\/(lock|unlock)$/, 'Risk matrix updated.'],
  [/^PUT \/v1\/tier-profiles\/[^/]+$/, 'Tier defaults saved.'],
  [/^PUT \/v1\/settings\/(finding-deadlines|issuing\/[^/]+)$/, 'Setting saved.'],
  [/^PUT \/v1\/messaging\/policy$/, 'Messaging policy saved.'],
  [/^PUT \/v1\/settings\/config\/[^/]+$/, 'Setting saved.'],
  [/^PUT \/v1\/settings\/suite-links$/, 'Suite links saved.'],
  [/^(PUT|DELETE) \/v1\/settings\/platform-link$/, 'Platform pairing updated.'],
  [/^(POST|PUT) \/v1\/connectors(\/[^/]+)?$/, 'Connection saved.'],
  [/^DELETE \/v1\/connectors\/[^/]+$/, 'Connection removed.'],
  [/^POST \/v1\/connectors\/[^/]+\/inbound-key$/, 'New inbound key made.'],
  [/^PUT \/v1\/mailboxes$/, 'Mailboxes saved.'],
  [/^POST \/v1\/users$/, 'Invitation sent.'],
  [/^PUT \/v1\/users\/[^/]+\/(roles|persona|phone)$/, 'Account saved.'],
  [/^POST \/v1\/users\/[^/]+\/disable$/, 'Account switched off.'],
  [/^POST \/v1\/users\/[^/]+\/enable$/, 'Account switched on again.'],
  [/^POST \/v1\/settings\/identity\/persona-mappings$/, 'Mapping added.'],
  [/^POST \/v1\/settings\/identity\/persona-mappings\/[^/]+\/remove$/, 'Mapping removed.'],
  [/^POST \/v1\/vendors\/[^/]+\/legal-holds$/, 'Legal hold placed.'],
  [/^POST \/v1\/vendors\/[^/]+\/legal-holds\/[^/]+\/release$/, 'Legal hold released.'],
  [/^POST \/v1\/vendors\/[^/]+\/exit\/decisions$/, 'Your decision on the offboarding is recorded.'],
  [/^PUT \/v1\/vendors\/[^/]+\/offboarding\/[^/]+$/, 'Offboarding item recorded.'],
  [/^PUT \/v1\/vendors\/[^/]+\/exit-plan$/, 'Exit plan saved.'],
  [/^POST \/v1\/vendors\/[^/]+\/exit-proposals$/, 'Offboarding proposed.'],
  [/^POST \/v1\/exit-proposals\/[^/]+\/(confirm|dismiss)$/, 'Offboarding proposal decided.'],
  [/^POST \/v1\/vendors\/[^/]+\/onboarding\/[^/]+(\/withdraw)?$/, 'Onboarding step recorded.'],
  [/^POST \/v1\/triggers(\/[^/]+\/dismiss)?$/, 'Reassessment trigger recorded.'],
  [/^PUT \/v1\/vendors\/[^/]+\/reassessment-policy$/, 'Reassessment cadence saved.'],
  [/^POST \/v1\/procurement\/credentials(\/[^/]+\/revoke)?$/, 'Procurement credential updated.'],
]

export function successMessage(method: string, path: string): string {
  const key = method.toUpperCase() + ' ' + path.split('?')[0]
  for (const [re, words] of NAMED) {
    if (re.test(key)) return words
  }
  return 'Done.'
}

// For tests: forget everything this module remembers.
export function resetFeedbackForTests(): void {
  items = []
  recentFailures.length = 0
  pendingSuccesses = []
  if (successTimer !== null) globalThis.clearTimeout(successTimer)
  successTimer = null
  left = null
  emit()
}
